/**
 * More details (admin mode): the member's custom fields (membermanagement 003).
 *
 * Fields are defined once for all of CCMS (Members › Member fields); a member only shows the fields that have a
 * value, plus "Add a field" to set another. Sensitive fields (POPIA) are shown only to people who may edit
 * members; the API leaves them out for everyone else and says how many are hidden.
 * API: api/members/{id}/custom-fields (read: Member.Read; change: Member.Write) and api/member-custom-fields.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlus, IconEdit, IconTrash, IconAlertCircle, IconListDetails, IconLock, IconSettings, IconEyeOff } from '@tabler/icons-react';
import {
  Card, Stack, Group, Button, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, Badge, EmptyState, Select, Divider, Anchor,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import {
  useMemberService, useMemberCustomFieldService,
  type MemberCustomFieldEntryView, type MemberCustomFieldDefinitionView, type MemberCustomFieldValuesResult,
} from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { errorMessage } from '../memberFormat';
import { formatCustomEntry } from '../memberDetailsFormat';
import { CustomFieldValuesInput, type CustomFieldInputDef } from '../CustomFieldValueInput';

/** Query key of the active definitions (shared with the Member fields page, which invalidates it). */
export const MEMBER_CUSTOM_FIELD_DEFINITIONS_KEY = ['members', 'custom-field-definitions'] as const;

const ADMIN_PAGE = '/admin/members/custom-fields';

interface Editing {
  definition: CustomFieldInputDef & { customFieldID: number; isSensitive: boolean };
  values: string[];
  isNew: boolean;
}

function toInputDef(d: MemberCustomFieldDefinitionView | MemberCustomFieldEntryView, placeholder?: string | null): Editing['definition'] {
  return {
    customFieldID: d.customFieldID,
    label: d.label,
    fieldTypeCode: d.fieldTypeCode,
    options: d.options ?? [],
    placeholder: placeholder ?? null,
    helpText: d.helpText,
    allowMultiple: d.allowMultiple,
    isSensitive: d.isSensitive,
  };
}

export function MoreDetailsCard({ api, member, perms }: MemberTabProps) {
  const memberService = useMemberService();
  const fieldService = useMemberCustomFieldService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const memberId = member.memberID;

  const values = useQuery({ queryKey: api.keys.customFields, queryFn: () => memberService.getMemberCustomFields(memberId), enabled: memberId > 0 });
  const definitions = useQuery({
    queryKey: MEMBER_CUSTOM_FIELD_DEFINITIONS_KEY,
    queryFn: () => fieldService.getAll({ includeInactive: false }),
    enabled: perms.edit,
    staleTime: 5 * 60 * 1000,
  });

  const [editing, setEditing] = useState<Editing | null>(null);
  const [picking, setPicking] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const afterWrite = (result: MemberCustomFieldValuesResult, title: string) => {
    queryClient.setQueryData(api.keys.customFields, result);
    queryClient.invalidateQueries({ queryKey: api.keys.member });
    queryClient.invalidateQueries({ queryKey: api.keys.audit });
    toast.success(title);
  };

  const save = useMutation({
    mutationFn: (e: Editing) => memberService.setMemberCustomField(memberId, e.definition.customFieldID, { values: e.values.filter((v) => v.trim() !== '') }),
    onSuccess: (result, e) => { afterWrite(result, `${e.definition.label} saved`); setEditing(null); },
    onError: (err) => toast.error('Could not save the value', errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (entry: MemberCustomFieldEntryView) => memberService.removeMemberCustomField(memberId, entry.customFieldID),
    onSuccess: (result, entry) => afterWrite(result, `${entry.label} removed`),
    onError: (err) => toast.error('Could not remove the value', errorMessage(err)),
  });

  const result = values.data;
  const filled = result?.fields ?? [];
  const canSeeSensitive = result?.canSeeSensitive ?? false;

  /** Fields that can still be added: active, not filled yet, sensitive ones only for people who may see them. */
  const available = useMemo(() => (definitions.data ?? [])
    .filter((d) => d.isActive && !filled.some((f) => f.customFieldID === d.customFieldID) && (canSeeSensitive || !d.isSensitive)),
  [definitions.data, filled, canSeeSensitive]);

  const grouped = useMemo(() => {
    const groups = new Map<string, MemberCustomFieldEntryView[]>();
    for (const f of filled) {
      const key = f.category?.trim() || '';
      groups.set(key, [...(groups.get(key) ?? []), f]);
    }
    return [...groups.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
  }, [filled]);

  const placeholderFor = (id: number) => definitions.data?.find((d) => d.customFieldID === id)?.placeholder ?? null;

  const openPicker = () => { setPickedId(null); setPicking(true); save.reset(); };
  const startAdd = () => {
    const def = available.find((d) => String(d.customFieldID) === pickedId);
    if (!def) return;
    setPicking(false);
    setEditing({ definition: toInputDef(def, def.placeholder), values: [], isNew: true });
  };
  const openEdit = (entry: MemberCustomFieldEntryView) => {
    save.reset();
    setEditing({ definition: toInputDef(entry, placeholderFor(entry.customFieldID)), values: entry.values.map((v) => v.value), isNew: false });
  };

  const hasValue = !!editing && editing.values.some((v) => v.trim() !== '');

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>More details</Text>
            <Text size="sm" c="dimmed">Other details the church keeps. Only fields with a value show here.</Text>
          </div>
          <Group gap="xs">
            {perms.customFieldsManage && (
              <Tooltip label="Add, change or switch off member fields for everyone">
                <Button size="sm" variant="subtle" leftSection={<IconSettings size={16} />} onClick={() => navigate(ADMIN_PAGE)}>Manage fields</Button>
              </Tooltip>
            )}
            {perms.edit && (
              <Button size="sm" variant="light" leftSection={<IconPlus size={16} />} onClick={openPicker}
                disabled={definitions.isSuccess && available.length === 0}>
                Add a field
              </Button>
            )}
          </Group>
        </Group>

        {values.isLoading && <Stack gap="sm"><Skeleton height={40} /><Skeleton height={40} /></Stack>}
        {values.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the details">{errorMessage(values.error)}</Alert>}

        {!values.isLoading && !values.isError && filled.length === 0 && (
          <EmptyState size="sm" icon={<IconListDetails size={32} />} title="No other details yet"
            description={perms.edit
              ? (definitions.isSuccess && (definitions.data ?? []).length === 0
                ? 'No member fields have been set up yet.'
                : 'Use Add a field to record something like an alternative WhatsApp number.')
              : 'Nothing else has been recorded for this person.'}
            actionLabel={perms.edit && available.length > 0 ? 'Add a field' : undefined} onAction={perms.edit && available.length > 0 ? openPicker : undefined} />
        )}

        {grouped.map(([category, entries], gi) => (
          <Stack key={category || 'none'} gap="xs">
            {(category || grouped.length > 1) && (
              <Divider label={category || 'Other'} labelPosition="left" mt={gi === 0 ? 0 : 'xs'} />
            )}
            {entries.map((entry) => {
              const shown = formatCustomEntry(entry);
              return (
                <Group key={entry.customFieldID} justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap={6} wrap="wrap">
                      <Text size="sm" c="dimmed">{entry.label}</Text>
                      {entry.isSensitive && <Badge size="xs" color="red" variant="light" leftSection={<IconLock size={10} />}>Sensitive</Badge>}
                      {!entry.isActive && <Badge size="xs" color="gray" variant="outline">Switched off</Badge>}
                    </Group>
                    {shown.length > 1 ? (
                      <Group gap={4} wrap="wrap">{shown.map((s, i) => <Badge key={i} variant="light" color="gray" radius="sm" style={{ textTransform: 'none' }}>{s}</Badge>)}</Group>
                    ) : entry.fieldTypeCode === 'url' ? (
                      <Anchor size="sm" href={entry.values[0]?.value} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{shown[0]}</Anchor>
                    ) : entry.fieldTypeCode === 'email' ? (
                      <Anchor size="sm" href={`mailto:${entry.values[0]?.value}`} style={{ wordBreak: 'break-all' }}>{shown[0]}</Anchor>
                    ) : (
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{shown[0]}</Text>
                    )}
                  </Stack>
                  {perms.edit && (
                    <Group gap={2} wrap="nowrap">
                      {entry.isActive && (
                        <Tooltip label="Edit"><ActionIcon variant="subtle" aria-label={`Edit ${entry.label}`} onClick={() => openEdit(entry)}><IconEdit size={16} /></ActionIcon></Tooltip>
                      )}
                      <Tooltip label="Remove">
                        <ActionIcon variant="subtle" color="red" aria-label={`Remove ${entry.label}`}
                          onClick={() => modal.confirm({
                            title: `Remove ${entry.label}?`,
                            body: entry.values.length > 1 ? 'All its values will be removed from this person.' : 'The value will be removed from this person.',
                            confirmLabel: 'Remove',
                            confirmColor: 'red',
                            onConfirm: () => remove.mutate(entry),
                          })}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </Group>
              );
            })}
          </Stack>
        ))}

        {(result?.hiddenSensitiveCount ?? 0) > 0 && (
          <Group gap={6}>
            <IconEyeOff size={14} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {result!.hiddenSensitiveCount === 1 ? '1 sensitive field is hidden.' : `${result!.hiddenSensitiveCount} sensitive fields are hidden.`}{' '}
              Only people who may edit members can see sensitive fields.
            </Text>
          </Group>
        )}
      </Stack>

      <Modal opened={picking} onClose={() => setPicking(false)} title="Add a field" centered size="md">
        <Stack gap="md">
          {definitions.isLoading && <Skeleton height={36} />}
          {definitions.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(definitions.error)}</Alert>}
          {definitions.isSuccess && available.length === 0 && (
            <Text size="sm" c="dimmed">Every member field already has a value for this person or no fields have been set up yet.</Text>
          )}
          {available.length > 0 && (
            <Select label="Field" placeholder="Choose a field" searchable value={pickedId} onChange={setPickedId}
              data={available.map((d) => ({ value: String(d.customFieldID), label: d.category ? `${d.label} (${d.category})` : d.label }))}
              description={available.find((d) => String(d.customFieldID) === pickedId)?.helpText ?? undefined} />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPicking(false)}>Cancel</Button>
            <Button disabled={!pickedId} onClick={startAdd}>Next</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={!!editing} onClose={() => setEditing(null)} centered size="md"
        title={editing ? (editing.isNew ? `Add ${editing.definition.label}` : `Edit ${editing.definition.label}`) : ''}>
        {editing && (
          <Stack gap="md">
            {editing.definition.isSensitive && (
              <Alert color="red" icon={<IconLock size={16} />}>
                Sensitive: only people who may edit members can see this field.
              </Alert>
            )}
            <CustomFieldValuesInput def={editing.definition} values={editing.values}
              onChange={(v) => setEditing({ ...editing, values: v })} />
            {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
              <Button loading={save.isPending} disabled={!hasValue && editing.isNew} onClick={() => save.mutate(editing)}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Card>
  );
}
